import { useState } from "react";
import { Plus, Search, FileText, Download, Eye } from "lucide-react";
import { CreateWorkOrderDialog } from "./CreateWorkOrderDialog";

// Utility for classes
const cn = (...classes) => classes.filter(Boolean).join(" ");

const Card = ({ className = "", children }) => (
  <div className={`rounded-lg border bg-white text-card-foreground shadow-sm ${className}`}>
    {children}
  </div>
);

const CardContent = ({ className = "", children }) => (
  <div className={`p-6 ${className}`}>{children}</div>
);

const Button = ({ variant = "default", size = "default", className = "", children, ...props }) => {
  const baseStyles = "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50";
  const variants = {
    default: "bg-sm-primary text-white shadow hover:bg-sm-primary/90",
    destructive: "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
    outline: "border border-input bg-transparent shadow-sm hover:bg-sm-accent hover:text-sm-accent-foreground",
    secondary: "bg-sm-secondary text-sm-secondary-foreground shadow-sm hover:bg-sm-secondary/80",
    ghost: "hover:bg-sm-accent hover:text-sm-accent-foreground",
    link: "text-sm-primary underline-offset-4 hover:underline",
  };
  const sizes = {
    default: "h-9 px-4 py-2",
    sm: "h-8 rounded-md px-3 text-xs",
    lg: "h-10 rounded-md px-8",
    icon: "h-9 w-9",
  };
  return (
    <button className={cn(baseStyles, variants[variant], sizes[size], className)} {...props}>
      {children}
    </button>
  );
};

const Input = ({ className = "", ...props }) => (
  <input className={`flex h-9 w-full rounded-md border border-input bg-transparent  py-1 pl-9 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 ${className}`} {...props} />
);

const Badge = ({ variant = "default", className = "", children }) => {
  const baseStyles = "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-normal transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2";
  const variants = {
    default: "border-transparent bg-sm-primary text-sm-primary-foreground hover:bg-sm-primary/80",
    secondary: "border-transparent bg-sm-secondary/10 text-sm-secondary hover:bg-sm-secondary/20",
    outline: "text-foreground",
  };
  const finalClass = className.includes("bg-") ? `${baseStyles} ${className}` : cn(baseStyles, variants[variant], className);
  return (
    <div className={finalClass}>
      {children}
    </div>
  );
};

export function ContractorWorkOrdersTab({
  workOrders,
  contractors,
  selectedContractorId,
  onCreateWorkOrder,
  onViewWorkOrder,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isWorkOrderDialogOpen, setIsWorkOrderDialogOpen] = useState(false);
  const filteredWorkOrders = workOrders
    .filter(wo => {
      if (searchQuery && !wo.id.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !wo.projectName.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      if (statusFilter !== "all" && wo.status.toLowerCase() !== statusFilter) {
        return false;
      }
      return true;
    });

  const formatCurrency = (amount) => {
    if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(2)}L`;
    }
    return `₹${amount.toLocaleString()}`;
  };

  const getStatusBadgeClass = (status) => {
    switch (status.toLowerCase()) {
      case "active":
        return "bg-green-500/10 text-green-600 border-green-500/30";
      case "completed":
        return "bg-sm-secondary/10 text-sm-secondary border-secondary/30";
      case "pending":
        return "bg-sm-accent/10 text-sm-accent border-sm-accent/30";
      case "on hold":
        return "bg-muted text-muted-foreground border-border";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="space-y-4">
      {/* Filters Row */}
      <div className="flex items-center justify-between gap-4">
        <span className="text-sm text-muted-foreground">
          {filteredWorkOrders.length} work orders
        </span>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search work orders..."
              className="h-9 text-sm w-56 border-border/60"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <select
            className="w-[120px] h-9 text-sm border border-input bg-background rounded-md px-3 py-1 shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
            <option value="on hold">On Hold</option>
          </select>
          <Button
            className="bg-sm-primary hover:bg-sm-primary/90 text-sm-primary-foreground h-9 text-sm gap-2"
            onClick={() => setIsWorkOrderDialogOpen(true)}
          >
            <Plus className="h-4 w-4" />
            Create Work Order
          </Button>
        </div>
      </div>

      {/* Work Orders Table */}
      <Card className="border-border/60 shadow-none">
        <CardContent className="p-0">
          <table className="w-full caption-bottom text-sm">
            <thead className="[&_tr]:border-b">
              <tr className="border-b transition-colors hover:bg-transparent border-border/60">
                <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground text-xs">Work Order ID</th>
                <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground text-xs">Project Name</th>
                <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground text-xs">Start Date</th>
                <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground text-xs">End Date</th>
                <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground text-xs">Contract Type</th>
                <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground text-xs">Payment Structure</th>
                <th className="h-10 px-2 align-middle font-medium text-muted-foreground text-xs text-right">Value</th>
                <th className="h-10 px-2 align-middle font-medium text-muted-foreground text-xs text-center">Status</th>
                <th className="h-10 w-[11%] align-middle font-medium text-muted-foreground text-xs text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="[&_tr:last-child]:border-0">
              {filteredWorkOrders.map((workOrder) => (
                <tr
                  key={workOrder.id}
                  className="group cursor-pointer hover:bg-muted/30 border-b border-border/40 transition-colors"
                  onClick={() => onViewWorkOrder && onViewWorkOrder(workOrder)}
                >
                  <td className="p-3 align-middle py-3">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-sm-secondary" />
                      <span className="text-sm font-medium text-sm-primary">#{workOrder.id}</span>
                    </div>
                  </td>
                  <td className="p-3 align-middle text-sm">{workOrder.projectName}</td>
                  <td className="p-3 align-middle text-sm text-muted-foreground">{formatDate(workOrder.startDate)}</td>
                  <td className="p-3 align-middle text-sm text-muted-foreground">{formatDate(workOrder.endDate)}</td>
                  <td className="p-3 align-middle">
                    <Badge variant="outline" className="text-xs font-normal border-border">
                      {workOrder.contractType}
                    </Badge>
                  </td>
                  <td className="p-3 align-middle">
                    <Badge variant="secondary" className="text-xs font-normal bg-sm-secondary/10 text-sm-secondary border-0">
                      {workOrder.paymentStructure}
                    </Badge>
                  </td>
                  <td className="p-3 align-middle text-right">
                    <div className="flex flex-col items-end">
                      <span className="text-sm font-medium">{formatCurrency(workOrder.value)}</span>
                      <span className="text-[10px] text-muted-foreground">
                        Paid: {formatCurrency(workOrder.paidAmount)}
                      </span>
                    </div>
                  </td>
                  <td className="p-3 align-middle text-center">
                    <Badge className={`text-xs font-normal ${getStatusBadgeClass(workOrder.status)}`}>
                      {workOrder.status}
                    </Badge>
                  </td>
                  <td className="p-3 align-middle text-center">
                    <div className="items-center w-full justify-center gap-1 hidden group-hover:flex transition-opacity">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onViewWorkOrder) onViewWorkOrder(workOrder);
                        }}
                      >
                        <Eye className="h-3.5 w-3.5 text-sm-primary hover:text-white" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Download className="h-3.5 w-3.5 text-sm-primary hover:text-white" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}

              {filteredWorkOrders.length === 0 && (
                <tr className="border-b border-border/40">
                  <td colSpan={9} className="p-4 align-middle py-12 text-center">
                    <div className="flex flex-col items-center">
                      <FileText className="h-10 w-10 text-muted-foreground/50 mb-2" />
                      <p className="text-sm text-muted-foreground">No work orders found</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Create Work Order Dialog */}
      <CreateWorkOrderDialog
        open={isWorkOrderDialogOpen}
        onOpenChange={setIsWorkOrderDialogOpen}
        contractors={contractors}
        selectedContractorId={selectedContractorId}
        onCreateWorkOrder={onCreateWorkOrder}
      />
    </div>
  );
}
